// Should a service-worker takeover reload the page the user is looking at?
//
// Background: iOS Safari re-installs the *same* worker script over and over —
// it reports `installed` + a live controller on almost every load, so
// `onupdatefound` fires without a deploy having happened. We used to reload on
// every one of those, which is the "the web keeps refreshing itself" bug: the
// reload re-registers the worker, iOS calls it new again, and around it goes.
// The old in-page `reloaded` guard could not help — each reload is a fresh
// document, so the guard was always back at `false`.
//
// The decision below is a pure function so the loop is testable without a
// service worker; `index.ts` supplies the environment.

export type UpdateReason =
    /** A genuinely different build is waiting — take it. */
    | 'new-build'
    /** The waiting worker is the build we already run. iOS's phantom update. */
    | 'same-build'
    /** Reloaded too recently; a worker too old to identify itself. */
    | 'throttled'
    /** Nobody is looking — let the next navigation pick the new build up. */
    | 'hidden'
    /** localStorage threw, so we cannot tell a loop from a deploy. */
    | 'no-storage'

export type UpdateDecision = { reload: boolean; reason: UpdateReason }

export type UpdateEnv = {
    /** Build id the waiting worker reported, or '' when it did not answer. */
    build: string
    /** Build id we last took over to, or '' / null when unknown. */
    lastBuild: string | null
    /** Timestamp of our last takeover reload, or null when unknown. */
    lastReloadAt: number | null
    /** False when localStorage is unreadable (private mode, blocked storage). */
    storageWorks: boolean
    visible: boolean
    now: number
}

/**
 * How long a page must run before another takeover may reload it.
 *
 * Only reached when the waiting worker is too old to report its build id —
 * i.e. the one update after this ships. Long enough that a loop is no longer a
 * loop, short enough that a real deploy still lands within a session.
 */
export const MIN_RELOAD_GAP_MS = 10 * 60 * 1000

export const decideReload = (env: UpdateEnv): UpdateDecision => {
    if (!env.storageWorks) return { reload: false, reason: 'no-storage' }
    if (env.build && env.build === env.lastBuild)
        return { reload: false, reason: 'same-build' }
    if (!env.visible) return { reload: false, reason: 'hidden' }
    if (
        env.lastReloadAt !== null &&
        env.now - env.lastReloadAt < MIN_RELOAD_GAP_MS
    ) {
        return { reload: false, reason: 'throttled' }
    }
    return { reload: true, reason: 'new-build' }
}
